import { AlertError, SupportingText, margins, Text } from '@ohgoodlord/design-system'
import { FieldErrors, UseFormRegister } from 'react-hook-form'
import styled from 'styled-components'
import { Asterix, InputLabel, InputWrapper, SelectWrapper } from './styles'
import { countries } from '../../../../../common/countryData'

const UKValue = 'United Kingdom of Great Britain and Northern Ireland'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${margins.l};
  width: 100%;
  padding-bottom: 80px;
`

const StyledHeading = styled.h5`
  margin: 15px 0 0 0;
`

export interface FormInputs {
  bedroomIdentifier: string
  nameOrNumber: string
  line1: string
  line2: string
  postcode: string
  city: string
  country: string
  example: string
  name?: string
}

interface Props {
  register: UseFormRegister<FormInputs>
  errors: FieldErrors<FormInputs>
  nameOrNumberLabel?: string
}

export const ManualAddressEntry = ({ register, errors, nameOrNumberLabel }: Props) => {
  return (
    <Wrapper>
      <StyledHeading>Enter address manually</StyledHeading>

      <InputWrapper>
        <InputLabel htmlFor="bedroomIdentifier">
          <SupportingText align="left">Room Number (optional)</SupportingText>
        </InputLabel>
        <input {...register('bedroomIdentifier')} />
      </InputWrapper>

      <InputWrapper>
        <InputLabel $hasAsterix htmlFor="nameOrNumber">
          <SupportingText align="left">
            {nameOrNumberLabel ?? 'House name or number'}
          </SupportingText>
          <Asterix>*</Asterix>
        </InputLabel>
        <input {...register('nameOrNumber', { required: true })} />
      </InputWrapper>
      {errors.nameOrNumber !== undefined && (
        <AlertError>
          <Text>House name or number is required</Text>
        </AlertError>
      )}

      <InputWrapper>
        <InputLabel $hasAsterix htmlFor="Line 1">
          <SupportingText align="left">Line 1</SupportingText>
          <Asterix>*</Asterix>
        </InputLabel>
        <input {...register('line1', { required: true })} />
      </InputWrapper>
      {errors.line1 !== undefined && (
        <AlertError>
          <Text>Line 1 is required</Text>
        </AlertError>
      )}

      <InputWrapper>
        <InputLabel htmlFor="line2">
          <SupportingText align="left">Line 2 (optional)</SupportingText>
        </InputLabel>
        <input {...register('line2')} />
      </InputWrapper>

      <InputWrapper>
        <InputLabel $hasAsterix htmlFor="city">
          <SupportingText align="left">City</SupportingText>
          <Asterix>*</Asterix>
        </InputLabel>
        <input {...register('city', { required: true })} />
      </InputWrapper>
      {errors.city !== undefined && (
        <AlertError>
          <Text>City is required</Text>
        </AlertError>
      )}

      <SelectWrapper>
        <InputLabel htmlFor="country" $hasAsterix>
          <SupportingText align="left">Country</SupportingText>
          <Asterix>*</Asterix>
        </InputLabel>
        <select {...register('country', { required: true })}>
          <option value={UKValue}>{UKValue}</option>
          <option disabled>-------</option>
          {countries.map(country => (
            <option key={country.id} value={country.name}>
              {country.name}
            </option>
          ))}
        </select>
      </SelectWrapper>
      {errors.country !== undefined && (
        <AlertError>
          <Text>Country is required</Text>
        </AlertError>
      )}

      <InputWrapper>
        <InputLabel $hasAsterix htmlFor="postcode">
          <SupportingText align="left">Postcode</SupportingText>
          <Asterix>*</Asterix>
        </InputLabel>
        <input {...register('postcode', { required: true })} />
      </InputWrapper>

      {errors.postcode !== undefined && (
        <AlertError>
          <Text>Postcode is required</Text>
        </AlertError>
      )}
    </Wrapper>
  )
}
